import { useCallback, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import type { JSX, SyntheticEvent } from 'react';

import MuiTabs from '@mui/material/Tabs';
import MuiTab from '@mui/material/Tab';
import Box from '@mui/material/Box';
import { useMediaQuery, useTheme } from '@mui/material';
import type { SxProps, Theme } from '@mui/material';

import { checkIfIsArray, ensureDefined, invariant } from '@e-socna/utils';
import { EMPTY_ARRAY, EMPTY_OBJECT } from '@e-socna/empty';

import { TabsStrategy } from './enums/strategy.enum';
import { TabsVariant } from './enums/variant.enum';
import { TabPanel } from './TabPanel';
import { DisabledPanel } from './DisabledPanel';

export { TabsStrategy, TabsVariant };

const RIGHT_NAV_WIDTH = '200px';

type Tab = Readonly<{
  key: string;
  icon?: JSX.Element;
  label: string;
  to?: string;
  value?: string;
  hiddenTab?: boolean;
}> &
  (
    | Readonly<{
        disabled?: false | null | undefined;
        panel: JSX.Element | readonly JSX.Element[];
      }>
    | Readonly<{
        disabled: true;
        panel?: JSX.Element | readonly JSX.Element[] | null | undefined;
      }>
  );

interface Props {
  readonly activeTab?: string;
  readonly id: string;
  readonly isInPrintMode?: boolean;
  readonly setActiveTab?: (value: string) => void;
  readonly strategy: TabsStrategy;
  readonly sx?: SxProps<Theme>;
  readonly tabs: readonly Tab[];
  readonly title: string;
  readonly variant?: TabsVariant;
}

export const Tabs = ({
  activeTab: pActiveTab,
  id,
  isInPrintMode = false,
  setActiveTab,
  strategy,
  sx = EMPTY_ARRAY,
  tabs,
  title,
  variant,
}: Readonly<Props>) => {
  invariant(!!(tabs.length && tabs[0]), 'Tabs must have at least one entry');

  const theme = useTheme();
  const matches = useMediaQuery(theme.breakpoints.up('lg'));
  const isVertical = variant === TabsVariant.Vertical;
  const displayVertically = isVertical && matches;

  const isStateful = strategy === TabsStrategy.Stateful;
  const isControlled = strategy === TabsStrategy.Controlled;
  const isRouterConnected = strategy === TabsStrategy.RouterConnected;
  const [statefulValue, setStatefulValue] = useState(tabs[0].key);
  const { pathname: currentPath, search } = useLocation();

  let activeTab: string;
  switch (strategy) {
    case TabsStrategy.Stateful: {
      activeTab = statefulValue;
      break;
    }
    case TabsStrategy.Controlled: {
      activeTab = ensureDefined({ pActiveTab }).pActiveTab;
      break;
    }
    case TabsStrategy.RouterConnected: {
      activeTab = `${currentPath}${search}`;
      break;
    }
    default: {
      // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
      throw new Error(`Unknown strategy: ${strategy}`);
    }
  }

  const handleChange = useCallback(
    (_event: SyntheticEvent, newValue: string) => {
      if (isStateful) {
        setStatefulValue(newValue);
      } else if (isControlled && setActiveTab) {
        setActiveTab(newValue);
      }
    },
    [isStateful, isControlled, setActiveTab],
  );

  const getTabValue = (_key: string, to?: string, value?: string) =>
    isRouterConnected
      ? ensureDefined({ to }).to
      : ensureDefined({ value }).value;

  const tabsBox = (
    <Box
      sx={{
        ...(displayVertically
          ? {
              borderLeft: 1,
              height: '100%',
              width: RIGHT_NAV_WIDTH,
              maxWidth: RIGHT_NAV_WIDTH,
              marginLeft: 3,
            }
          : { borderBottom: 1 }),
        borderColor: 'divider',
      }}
    >
      <MuiTabs
        allowScrollButtonsMobile
        aria-label={title}
        onChange={handleChange}
        orientation={displayVertically ? 'vertical' : 'horizontal'}
        scrollButtons
        value={activeTab}
        variant="scrollable"
      >
        {tabs
          .filter((tab) => !tab.hiddenTab)
          .map(({ key, icon, label, to, value, disabled }) => {
            const tabValue = getTabValue(key, to, value);
            const tabId = `${id}-${key}`;

            return (
              <MuiTab
                aria-controls={`${tabId}-panel`}
                id={tabId}
                key={tabId}
                label={label}
                value={tabValue}
                // eslint-disable-next-line react/jsx-props-no-spreading
                {...(icon ? { icon } : EMPTY_OBJECT)}
                // eslint-disable-next-line react/jsx-props-no-spreading
                {...(to ? { component: Link, to } : EMPTY_OBJECT)}
                // eslint-disable-next-line react/jsx-props-no-spreading
                {...(disabled ? { disabled } : EMPTY_OBJECT)}
              />
            );
          })}
      </MuiTabs>
    </Box>
  );

  return (
    <Box
      sx={
        isInPrintMode
          ? [{ display: 'block', width: '100%' }]
          : [
              {
                display: 'flex',
                flexGrow: 1,
                flexDirection: displayVertically ? 'row-reverse' : 'column',
                maxWidth: '100%',
                overflow: 'auto',
              },
              ...(checkIfIsArray(sx) ? sx : [sx]),
            ]
      }
    >
      {!isInPrintMode && tabsBox}
      {tabs.map(({ disabled, key, panel, to, value }) => {
        const panelValue = getTabValue(key, to, value);
        const tabId = `${id}-${key}`;

        return (
          <TabPanel
            activeTab={activeTab}
            key={tabId}
            sx={
              isInPrintMode
                ? {
                    display: panelValue !== activeTab ? 'none' : 'block',
                    width: '100%',
                  }
                : {
                    display: panelValue !== activeTab ? 'none' : 'flex',
                    ...(displayVertically && activeTab === panelValue
                      ? {
                          maxWidth: `calc(100% - ${RIGHT_NAV_WIDTH})`,
                          width: `calc(100% - ${RIGHT_NAV_WIDTH})`,
                        }
                      : EMPTY_OBJECT),
                    ...(!displayVertically && activeTab === panelValue
                      ? {
                          flexGrow: 1,
                        }
                      : EMPTY_OBJECT),
                    overflow: 'auto',
                  }
            }
            tabId={tabId}
            value={panelValue}
          >
            {disabled ? <DisabledPanel /> : panel}
          </TabPanel>
        );
      })}
    </Box>
  );
};
